import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  DollarSign,
  ScanLine,
} from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { DatePickerSheet } from '@/components/DatePickerSheet';
import { DocPageThumb } from '@/components/DocPagePreview';
import { PdfThumb } from '@/components/PdfThumb';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ContactRow } from '@/db/types';
import { listContacts, markContactUsed } from '@/lib/contacts';
import { parseLocalDate, toLocalYMD } from '@/lib/dates';
import { addDocument } from '@/lib/documents';
import { allExpenseCategories } from '@/lib/expenses';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { composeScanNotes, scanReceiptBase64, type ParsedReceipt } from '@/lib/ocr';
import { listGoals } from '@/lib/goals';
import { pickAndScanFile } from '@/lib/scan-intake';
import { addQuote, listQuotes } from '@/lib/quotes';
import { takeSmartScan } from '@/lib/smart-scan';
import { getActiveProperty } from '@/lib/tasks';
import { guessExpenseCategory } from '@/lib/vendor-category';
import { webNoOutline } from '@/lib/web-style';

/**
 * New quote — same anatomy as New expense, tuned for "maybe money":
 *   - Scan an estimate: Vision pre-fills vendor / amount / date, AND the
 *     photo is kept — persisted as an invoice Document attached to the
 *     quote on save, so the paper estimate is never lost.
 *   - Project field clusters competing bids ("Roof replacement") for
 *     the side-by-side comparison on the Quotes list.
 *   - Valid-until powers the auto-expire pass.
 */
export default function NewQuote() {
  const router = useRouter();
  const db = useSQLiteContext();
  // Wave 61: user-defined categories join the picker. Loaded once from
  // the active property row.
  const [customCatsJson, setCustomCatsJson] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      setCustomCatsJson(p?.custom_expense_categories_json ?? null);
    })();
  }, [db]);
  const notice = useNotice();

  const [category, setCategory] = useState<string>('Maintenance');
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [vendor, setVendor] = useState('');
  const [project, setProject] = useState('');
  const [amount, setAmount] = useState('');
  const [quotedOn, setQuotedOn] = useState(toLocalYMD(Date.now()));
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanHint, setScanHint] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [validOpen, setValidOpen] = useState(false);
  // The scanned estimate photo (or PDF via Smart Scan), kept so save()
  // can persist it as a Document and attach it to the quote.
  const [scanUri, setScanUri] = useState<string | null>(null);
  const [scanMime, setScanMime] = useState<string>('image/jpeg');
  /** Original filename from the picker — lets the docx/text "paper
   *  page" preview identify the format when the mime is generic. */
  const [scanName, setScanName] = useState<string | null>(null);
  // Optional link to a saved contact — ties the bid to the contractor's
  // card (phone, past jobs) instead of a free-text vendor string alone.
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [projectPool, setProjectPool] = useState<string[]>([]);
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      if (prop) {
        setContacts(await listContacts(db, prop.id));
        // Wave 62: seed project suggestions from existing quote groups
        // AND the user's actual Projects — so a new bid can join either
        // with one tap instead of retyping the exact name.
        const [quotes, goals] = await Promise.all([
          listQuotes(db, prop.id),
          listGoals(db, prop.id),
        ]);
        const names: string[] = [];
        for (const q of quotes) {
          const p = q.project?.trim();
          if (p && !names.some((n) => n.toLowerCase() === p.toLowerCase())) names.push(p);
        }
        for (const g of goals) {
          if (g.status === 'done') continue;
          const t = g.title.trim();
          if (t && !names.some((n) => n.toLowerCase() === t.toLowerCase())) names.push(t);
        }
        setProjectPool(names);
      }
    })();
  }, [db]);

  /** Chips shown under the Project field — when empty, the most recent
   *  handful; while typing, substring matches (hidden on exact match). */
  const projectSuggestions = (() => {
    const q = project.trim().toLowerCase();
    if (!q) return projectPool.slice(0, 6);
    if (projectPool.some((n) => n.toLowerCase() === q)) return [];
    return projectPool.filter((n) => n.toLowerCase().includes(q)).slice(0, 6);
  })();

  // Smart Scan hand-off (/scan classified a photo as an estimate and
  // routed here) — consume once on mount, pre-fill, attach the photo.
  useEffect(() => {
    const handoff = takeSmartScan('quote');
    if (!handoff) return;
    setScanUri(handoff.uri);
    if (handoff.mime) setScanMime(handoff.mime);
    if (handoff.name) setScanName(handoff.name);
    const parsed = handoff.parsed;
    if (parsed) {
      if (parsed.vendor) {
        setVendor(parsed.vendor);
        const guess = guessExpenseCategory(parsed.vendor);
        if (guess) setCategory(guess);
      }
      // Wave 53: estimates carry the job name and validity window —
      // fill the Project + Valid-until fields the form already has.
      if (parsed.project?.trim()) setProject(parsed.project.trim());
      if (parsed.description?.trim()) setDescription(parsed.description.trim());
      if (parsed.amount_cents != null) setAmount((parsed.amount_cents / 100).toFixed(2));
      if (parsed.date != null) setQuotedOn(toLocalYMD(parsed.date));
      if (parsed.valid_until != null) setValidUntil(toLocalYMD(parsed.valid_until));
      const scanNotes = composeScanNotes(parsed);
      if (scanNotes) setNotes(scanNotes);
      setScanHint('Smart scan filed this as a quote — check the details, then save.');
    } else {
      setScanHint('Photo attached from Smart scan — fill in the details, then save.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedContact = contacts.find((c) => c.id === contactId) ?? null;

  const canSave = amount.trim().length > 0 && category;

  const runScan = async (source: 'camera' | 'library') => {
    setScanHint(null);
    let asset: ImagePicker.ImagePickerAsset | undefined;
    try {
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchCameraAsync({ quality: 0.7, base64: true });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.7,
          base64: true,
        });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      }
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }

    const base64 = asset?.base64 ?? null;
    if (!base64 || !asset) {
      void notice({
        title: "Couldn't read photo",
        message: 'Your photo library returned no image data. Try a different file.',
        tone: 'error',
      });
      return;
    }

    setScanning(true);
    try {
      const result = await scanReceiptBase64(base64);
      if (!result.ok) {
        if (result.error.kind === 'no-key') {
          void notice({
            title: 'No Vision API key',
            message: 'Add a Google Vision key in Settings → Admin → API keys to enable estimate scanning.',
            tone: 'warning',
          });
        } else {
          void notice({
            title: result.error.kind === 'network' ? "Couldn't reach Vision" : "Couldn't read the estimate",
            message: result.error.message,
            tone: 'error',
          });
        }
        return;
      }
      // Keep the photo regardless of how much parsed — the scan itself
      // is worth saving even when OCR whiffs on the amount.
      setScanUri(asset.uri);
      applyParsedToForm(result.parsed);
    } finally {
      setScanning(false);
    }
  };

  /** Shared prefill for camera scans and file uploads (Wave 54). */
  const applyParsedToForm = (parsed: ParsedReceipt) => {
    {
      const filled: string[] = [];
      if (parsed.vendor && !vendor) { setVendor(parsed.vendor); filled.push('vendor'); }
      if (parsed.vendor && !categoryTouched) {
        const guess = guessExpenseCategory(parsed.vendor);
        if (guess && guess !== category) {
          setCategory(guess);
          filled.push('category');
        }
      }
      if (!project.trim() && parsed.project?.trim()) {
        setProject(parsed.project.trim());
        filled.push('project');
      }
      if (!description && parsed.description?.trim()) {
        setDescription(parsed.description.trim());
        filled.push('work description');
      }
      if (parsed.amount_cents != null && !amount.trim()) {
        setAmount((parsed.amount_cents / 100).toFixed(2));
        filled.push('amount');
      }
      if (parsed.date != null) {
        setQuotedOn(toLocalYMD(parsed.date));
        filled.push('date');
      }
      if (parsed.valid_until != null && !validUntil) {
        setValidUntil(toLocalYMD(parsed.valid_until));
        filled.push('valid until');
      }
      if (!notes.trim()) {
        const scanNotes = composeScanNotes(parsed);
        if (scanNotes) {
          setNotes(scanNotes);
          filled.push('notes');
        }
      }
      setScanHint(
        filled.length > 0
          ? `Pre-filled: ${filled.join(', ')}. The estimate file will be saved with the quote.`
          : 'Scan saved — nothing obvious to pre-fill. Check the form.'
      );
    }
  };

  /** Any-file intake (Wave 54): images and PDFs OCR + prefill; other
   *  documents attach so they save with the quote. */
  const runFileScan = async () => {
    setScanHint(null);
    setScanning(true);
    try {
      const res = await pickAndScanFile();
      if (res.status === 'canceled') return;
      if (res.status === 'error') {
        void notice({ title: 'Picker failed', message: res.message, tone: 'error' });
        return;
      }
      setScanUri(res.uri);
      setScanMime(res.mime ?? 'application/octet-stream');
      setScanName(res.name ?? null);
      if (res.parsed) {
        applyParsedToForm(res.parsed);
      } else {
        setScanHint('File attached — it will save with this quote. Fill in the details.');
      }
    } finally {
      setScanning(false);
    }
  };

  const pickScanSource = () => {
    if (Platform.OS === 'web') {
      void runFileScan();
      return;
    }
    void runScan('camera');
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) {
        void notice({
          title: 'No home set up yet',
          message: 'Finish onboarding first — quotes attach to a home.',
          tone: 'warning',
        });
        return;
      }
      const cents = Math.round(parseFloat(amount.replace(/[^0-9.]/g, '')) * 100);
      if (isNaN(cents)) {
        void notice({
          title: "Couldn't read the amount",
          message: 'Enter a number like 8500 or 8,500.00.',
          tone: 'warning',
        });
        return;
      }
      // Parse as LOCAL NOON, not bare Date.parse — a bare 'YYYY-MM-DD'
      // parses as UTC midnight, which renders as the PREVIOUS day for
      // anyone west of Greenwich. Noon keeps the calendar date stable
      // in every timezone.
      const quoted = Date.parse(quotedOn + 'T12:00:00');
      if (isNaN(quoted)) {
        void notice({
          title: "Couldn't read the quote date",
          message: 'Pick the date again from the calendar.',
          tone: 'warning',
        });
        return;
      }
      const until = validUntil ? Date.parse(validUntil + 'T12:00:00') : NaN;

      // Persist the scanned estimate photo (if any) as a Document BEFORE
      // inserting the quote — same slow-file-I/O-outside-transaction
      // discipline the receipt importer uses.
      let quoteDocId: string | null = null;
      if (scanUri) {
        try {
          const ext =
            scanUri.match(/\.[a-zA-Z0-9]+$/)?.[0] ??
            (scanMime.includes('pdf') ? '.pdf' : '.jpg');
          const fileName = `quote_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
          const fileUri = await persistPickedFile(scanUri, fileName);
          // Same Wave 52.1 pattern as the expense form: the title says
          // what work was quoted, so Files reads without opening it.
          const base = vendor.trim()
            ? `Estimate — ${vendor.trim()}`
            : `Estimate — ${new Date(quoted).toLocaleDateString()}`;
          const desc = (description.trim() || project.trim());
          const withDesc =
            desc && desc.toLowerCase() !== vendor.trim().toLowerCase()
              ? `${base} · ${desc}`
              : base;
          quoteDocId = await addDocument(db, {
            propertyId: prop.id,
            kind: 'estimate',
            title: withDesc.slice(0, 80),
            file_uri: fileUri,
            mime_type: scanMime,
            vendor: vendor.trim() || null,
            cost_cents: cents,
            notes: desc || null,
          });
        } catch (err) {
          // A failed photo persist shouldn't lose the quote itself.
          console.warn('quote doc persist failed', err);
        }
      }

      const id = await addQuote(db, {
        propertyId: prop.id,
        contactId,
        project: project.trim() || null,
        category,
        vendor: vendor.trim() || null,
        description: description.trim() || null,
        amount_cents: cents,
        quoted_on: quoted,
        valid_until: isNaN(until) ? null : until,
        quote_doc_id: quoteDocId,
        notes: notes.trim() || null,
      });
      if (contactId) void markContactUsed(db, contactId).catch(() => {});
      router.replace(`/quotes/${id}` as never);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/quotes')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>New quote</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Save an estimate</Text>
        <Text style={styles.sub}>
          Contractor bids, service estimates, project pricing. Group
          competing bids under one project name to compare — and keep the
          history for the next time this work comes up.
        </Text>

        <Pressable
          onPress={pickScanSource}
          disabled={scanning}
          style={({ pressed }) => [
            styles.scanBtn,
            pressed && { transform: [{ scale: 0.98 }] },
            scanning && { opacity: 0.6 },
          ]}>
          <ScanLine size={18} color={Palette.forest} strokeWidth={2} />
          <View style={{ flex: 1 }}>
            <Text style={styles.scanTitle}>
              {scanning ? 'Reading estimate…' : scanUri ? 'Re-scan the estimate' : 'Scan the estimate'}
            </Text>
            <Text style={styles.scanSub}>
              {scanning
                ? 'Vision is parsing the text — a couple of seconds.'
                : scanUri
                ? 'File attached — it will be saved with this quote.'
                : Platform.OS === 'web'
                ? 'Photo, PDF, or document — pre-fills the form and keeps the file.'
                : 'Snap the paper quote — pre-fills the form and keeps the scan.'}
            </Text>
            {Platform.OS !== 'web' && !scanning ? (
              <Pressable onPress={() => void runFileScan()} hitSlop={8}>
                <Text style={styles.scanAltLink}>Or upload a file (PDF, image, doc)</Text>
              </Pressable>
            ) : null}
          </View>
          {/* Attached-file preview (Wave 53) — see what will be saved
              with this quote without leaving the form. */}
          {scanUri && !scanning ? (
            scanMime.includes('pdf') ? (
              <PdfThumb
                uri={scanUri}
                style={styles.scanPreview}
                fallback={
                  <View style={[styles.scanPreview, styles.scanPreviewFallback]}>
                    <ScanLine size={18} color={Palette.forest} strokeWidth={1.6} />
                  </View>
                }
              />
            ) : scanMime.startsWith('image/') ? (
              <Image source={{ uri: scanUri }} style={styles.scanPreview} resizeMode="cover" />
            ) : (
              // docx / spreadsheets / text files: render extracted text
              // as a paper page — <Image> with a docx URI drew a blank
              // white box (Wave 60).
              <DocPageThumb
                uri={scanUri}
                mime={scanMime}
                name={scanName}
                style={styles.scanPreview}
                fallback={
                  <View style={[styles.scanPreview, styles.scanPreviewFallback]}>
                    <ScanLine size={18} color={Palette.forest} strokeWidth={1.6} />
                  </View>
                }
              />
            )
          ) : null}
        </Pressable>
        {scanHint ? (
          <View style={styles.scanHint}>
            <Text style={styles.scanHintText}>{scanHint}</Text>
          </View>
        ) : null}

        <View style={styles.card}>
          <Text style={styles.fieldLabel}>QUOTED AMOUNT</Text>
          <View style={styles.amountRow}>
            <DollarSign size={18} color={Palette.inkSoft} strokeWidth={2} />
            <TextInput
              value={amount}
              onChangeText={setAmount}
              placeholder="0.00"
              placeholderTextColor={Palette.inkFaint}
              inputMode="decimal"
              style={[styles.amountInput, webNoOutline]}
            />
          </View>

          <Field
            label="Project"
            value={project}
            onChangeText={setProject}
            placeholder="e.g. Roof replacement — groups competing bids"
          />
          {/* Suggestion chips (Wave 62): existing quote projects + the
              user's Projects list. Free text stays king — a chip just
              fills the field so competing bids land in the same group
              (exact-match grouping) without retyping. */}
          {projectSuggestions.length > 0 ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.projSugRow}>
              {projectSuggestions.map((p) => (
                <Pressable
                  key={p}
                  onPress={() => setProject(p)}
                  style={({ pressed }) => [styles.projSugChip, pressed && { opacity: 0.85 }]}>
                  <Text style={styles.projSugText} numberOfLines={1}>{p}</Text>
                </Pressable>
              ))}
            </ScrollView>
          ) : null}
          <Field label="Vendor / contractor" value={vendor} onChangeText={setVendor} placeholder="ABC Roofing, Joe's HVAC, etc." />
          {contacts.length > 0 ? (
            <Pick
              label="Link a saved contact (optional)"
              value={
                selectedContact
                  ? `${selectedContact.name}${selectedContact.role ? ` · ${selectedContact.role}` : ''}`
                  : 'None — tap to pick'
              }
              onPress={() => setContactOpen(true)}
            />
          ) : null}
          <Field label="Work description" value={description} onChangeText={setDescription} placeholder="What's the quote for?" />
          <Pick label="Category" value={category} onPress={() => setCatOpen(true)} />
        </View>

        <View style={styles.card}>
          <Text style={styles.fieldLabel}>QUOTE DATE</Text>
          <Pressable
            onPress={() => setDateOpen(true)}
            style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
            <Text style={styles.pickText}>
              {new Date(quotedOn + 'T12:00:00').toLocaleDateString(undefined, {
                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
              })}
            </Text>
            <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
          </Pressable>

          <View style={{ marginTop: Spacing.three }}>
            <Text style={styles.fieldLabel}>VALID UNTIL (OPTIONAL)</Text>
            <Pressable
              onPress={() => setValidOpen(true)}
              style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
              <Text style={[styles.pickText, !validUntil && { color: Palette.inkFaint }]}>
                {validUntil
                  ? new Date(validUntil + 'T12:00:00').toLocaleDateString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                    })
                  : 'No expiry — tap to set'}
              </Text>
              <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
            </Pressable>
            {validUntil ? (
              <Pressable onPress={() => setValidUntil(null)} hitSlop={6} style={{ marginTop: 6, alignSelf: 'flex-start' }}>
                <Text style={styles.clearLink}>Clear expiry</Text>
              </Pressable>
            ) : null}
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.fieldLabel}>NOTES</Text>
          <TextInput
            value={notes}
            onChangeText={setNotes}
            placeholder="Scope details, what's included, gut feeling about the contractor…"
            placeholderTextColor={Palette.inkFaint}
            multiline
            style={[styles.fieldInput, webNoOutline, { height: 80, textAlignVertical: 'top' }]}
          />
        </View>
      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save quote'}</Text>
        </Pressable>
      </StickyBottomBar>

      <Modal animationType="fade" transparent visible={catOpen} onRequestClose={() => setCatOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setCatOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Category</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              {allExpenseCategories(customCatsJson).map((c) => {
                const sel = category === c;
                return (
                  <Pressable
                    key={c}
                    onPress={() => {
                      setCategory(c);
                      setCategoryTouched(true);
                      setCatOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.modalRow,
                      pressed && { backgroundColor: '#EEF3EE' },
                      sel && { backgroundColor: '#EEF3EE' },
                    ]}>
                    <Text style={styles.modalRowText}>{c}</Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal animationType="fade" transparent visible={contactOpen} onRequestClose={() => setContactOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setContactOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Link a contact</Text>
            <ScrollView style={{ maxHeight: 380 }}>
              <Pressable
                onPress={() => {
                  setContactId(null);
                  setContactOpen(false);
                }}
                style={({ pressed }) => [
                  styles.modalRow,
                  (pressed || contactId === null) && { backgroundColor: '#EEF3EE' },
                ]}>
                <Text style={styles.modalRowText}>None</Text>
              </Pressable>
              {contacts.map((c) => {
                const sel = contactId === c.id;
                return (
                  <Pressable
                    key={c.id}
                    onPress={() => {
                      setContactId(c.id);
                      // A linked contact usually IS the vendor — fill the
                      // free-text field if the user hasn't typed one.
                      if (!vendor.trim()) setVendor(c.company?.trim() || c.name);
                      setContactOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.modalRow,
                      (pressed || sel) && { backgroundColor: '#EEF3EE' },
                    ]}>
                    <Text style={styles.modalRowText}>{c.name}</Text>
                    {c.role || c.company ? (
                      <Text style={styles.modalRowSub}>
                        {[c.role, c.company].filter(Boolean).join(' · ')}
                      </Text>
                    ) : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <DatePickerSheet
        open={dateOpen}
        value={(() => { const t = parseLocalDate(quotedOn); return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setQuotedOn(toLocalYMD(ms));
          setDateOpen(false);
        }}
        onClose={() => setDateOpen(false)}
        title="Quote date"
      />
      <DatePickerSheet
        open={validOpen}
        value={(() => { const t = validUntil ? parseLocalDate(validUntil) : NaN; return isNaN(t) ? null : t; })()}
        onPick={(ms) => {
          setValidUntil(toLocalYMD(ms));
          setValidOpen(false);
        }}
        onClose={() => setValidOpen(false)}
        title="Valid until"
      />
    </SafeAreaView>
  );
}

function Field({
  label, value, onChangeText, placeholder,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        style={[styles.fieldInput, webNoOutline]}
      />
    </View>
  );
}

function Pick({
  label, value, onPress,
}: {
  label: string;
  value: string | null;
  onPress: () => void;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => [styles.pickBox, pressed && { opacity: 0.92 }]}>
        <Text style={styles.pickText}>{value ?? 'Tap to pick'}</Text>
        <ChevronDown size={14} color={Palette.inkFaint} strokeWidth={1.7} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  amountRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: Palette.sand, borderRadius: 11,
    borderWidth: 1.5, borderColor: Palette.line,
    paddingHorizontal: 12, marginTop: 5,
  },
  amountInput: {
    flex: 1, paddingVertical: 14, paddingLeft: 8,
    fontFamily: Fonts.serifBold, fontSize: 28, color: Palette.forest,
    letterSpacing: -0.5,
  },
  projSugRow: { gap: 6, paddingVertical: 8 },
  projSugChip: {
    maxWidth: 220,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#EEF3EE',
    borderWidth: 1.2, borderColor: 'rgba(44,71,56,0.3)',
  },
  projSugText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  pickBox: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 12,
  },
  pickText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  clearLink: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.clay },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink, marginBottom: 10 },
  modalRow: { paddingVertical: 11, paddingHorizontal: 12, borderRadius: 10 },
  modalRowText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  modalRowSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  scanAltLink: {
    marginTop: 6,
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    textDecorationLine: 'underline',
  },
  scanPreview: {
    width: '100%', height: 220,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  scanPreviewFallback: {
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: Palette.sand,
  },
  scanBtn: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    flexDirection: 'row', alignItems: 'center', gap: 12,
    flexWrap: 'wrap',
    backgroundColor: '#EAF0EA',
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: 16, padding: 14,
  },
  scanTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  scanSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 16,
  },
  scanHint: {
    marginTop: 8, marginHorizontal: Spacing.four,
    padding: 12, borderRadius: 11,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1, borderColor: 'rgba(225,163,61,0.32)',
  },
  scanHintText: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: '#5A4012', lineHeight: 17,
  },
});
